import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import { salesManagement, companyManagementUrl } from "../../../config";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import {
  fetchDataEngine,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";
import {
  allwarehousedistribution,
  SearchWarehouseDistribution,
  GetWarehouseDistributionStats,
} from "../../../data/Axios/queries";
import { format } from "date-fns";

const base_url = salesManagement.uri;
const company_baseurl = companyManagementUrl.uri;

const actions = {
  entity: "Warehouse Distributed Logs",
  edit: {
    key: "EDIT",
    button_name: "Edit",
    Show_Button: false,
    permission: "EditPermission",
  },
  add: {
    key: "ADD",
    button_name: "Add",
    Show_Button: false,
    permission: "CreatePermission",
  },
  activateDeactivate: {
    key: "ACTIVATEDEACTIVATE",
    button_name: "Deactivate",
    Show_Button: false,
    permission: "ActivatePermission",
    modelName: "inventoryIds",
    inputObjectName: "bulkActivateDeactivateInventory",
  },
  AssignTask: {
    key: "ASSIGN_TASK",
    button_name: "Assign Task",
    Show_Button: false,
    permission: "hasNoPermission",
    roleGroupId: [1, 2],
  },
  showStatus: true,
  showMore: {
    key: "SHOWMORE",
    button_name: "Show More",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
    title: "Warehouse Distribution Details",
  },
};
//deployment test
function WarehouseDistributionTable() {
  const [distributions, setDistributions] = useState([]);
  const [filteredDistributions, setFilteredDistributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all"); // all, today, week, month
  const [sizeRangeFilter, setSizeRangeFilter] = useState("");
  const [sizeRanges, setSizeRanges] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const [stats, setStats] = useState({
    totalDistributions: 0,
    totalCartons: 0,
    totalCost: 0,
    uniqueProducts: 0,
    uniqueBranches: 0,
    todayDistributions: 0,
    weekDistributions: 0,
    averageCostPerCarton: 0,
  });

  // Theme colors
  const theme = {
    primary: "#1976D2",
    secondary: "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    lightBg: "#E3F2FD",
    cardBg: "#FFFFFF",
    purple: "#9C27B0",
  };

  // ✅ Fetch branches (warehouses)
  const fetchBranches = async () => {
    try {
      const payload = { isWarehouse: 1 }; // Warehouse branches only
      const returnFields = ["id", "branchName"];
      const res = await fetchDataEngine(
        "allBranchesByColumnValues",
        "branchModel",
        payload,
        returnFields,
        company_baseurl
      );
      setBranches(res || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  // ✅ Calculate statistics (client-side fallback - only used if server stats fail)
  const calculateStats = (data) => {
    const totalDistributions = data.length;
    const totalCartons = data.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
    const totalCost = data.reduce(
      (sum, item) => sum + (item.averageCost || 0) * (item.quantity || 0),
      0
    );
    const uniqueProducts = new Set(data.map((item) => item.productCode)).size;
    const uniqueBranches = new Set(data.map((item) => item.branchId)).size;
    const averageCostPerCarton =
      totalCartons > 0 ? totalCost / totalCartons : 0;

    // Date calculations
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const todayDistributions = data.filter((item) => {
      const itemDate = new Date(item.createdDate);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate.getTime() === today.getTime();
    }).length;

    const weekDistributions = data.filter((item) => {
      const itemDate = new Date(item.createdDate);
      return itemDate >= weekAgo;
    }).length;

    setStats({
      totalDistributions,
      totalCartons,
      totalCost,
      uniqueProducts,
      uniqueBranches,
      todayDistributions,
      weekDistributions,
      averageCostPerCarton,
    });
  };

  // Fetch stats from server
  const fetchStats = async () => {
    try {
      const query = GetWarehouseDistributionStats(selectedBranch || 0);
      const result = await graphqlQuery(query, base_url);

      // graphqlQuery already unwraps the top-level field and usually returns the array directly.
      // Normalize to always work with an array, regardless of backend shape.
      const statsArray = Array.isArray(result)
        ? result
        : result?.warehouseDistributionStats || [];

      if (statsArray && statsArray.length > 0) {
        const statsData = statsArray[0];
        setStats({
          totalDistributions: statsData.totalDistributions || 0,
          totalCartons: statsData.totalCartons || 0,
          totalCost: statsData.totalCost || 0,
          uniqueProducts: statsData.uniqueProducts || 0,
          uniqueBranches: 0, // This is not returned by the current stats query
          todayDistributions: statsData.todayDistributions || 0,
          weekDistributions: statsData.weekDistributions || 0,
          averageCostPerCarton: statsData.totalCartons > 0
            ? statsData.totalCost / statsData.totalCartons
            : 0,
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      // Fallback to client-side calculation handled by existing flow
      setStats((prev) => ({
        ...prev,
        totalDistributions: 0,
        totalCartons: 0,
        totalCost: 0,
        uniqueProducts: 0,
        todayDistributions: 0,
        weekDistributions: 0,
        averageCostPerCarton: 0,
      }));
    }
  };

  const fetchDistributions = async () => {
    try {
      setLoading(true);
      setError("");

      // Fetch distributions with server-side pagination
      const query = SearchWarehouseDistribution({
        branchId: selectedBranch,
        search: searchQuery || "",
        limit: pageSize,
        pageNumber: page + 1, // MUI is 0-based, backend is 1-based
      });
      const result = await graphqlQuery(query, base_url);

      // graphqlQuery auto-unwraps: result IS the Connection object { items: [...], totalCount: N }
      const connection = result || {};
      const rows = connection.items || (Array.isArray(result) ? result : []);
      const totalCount = connection.totalCount || rows.length;

      if (rows && rows.length > 0) {
        const distributionData = rows.map((item) => ({
          ...item,
          formattedDate: item.createdDate
            ? format(new Date(item.createdDate), "yyyy-MM-dd HH:mm")
            : "",
          sizeRangeDisplay:
            item.sizeRange || `${item.minSize || ""}-${item.maxSize || ""}`,
        }));

        setDistributions(distributionData);
        setRowCount(totalCount);

        // Extract unique size ranges (only from current data)
        const uniqueSizeRanges = [
          ...new Set(
            distributionData
              .map((item) => item.sizeRangeDisplay)
              .filter((range) => range && range !== "-")
          ),
        ];
        setSizeRanges(uniqueSizeRanges);

        // Apply CLIENT-SIDE filters (branch, date, size) to the fetched data
        applyFilters(
          distributionData,
          selectedBranch,
          // Server already filtered by search, so we skip local text filter
          "",
          dateFilter,
          sizeRangeFilter
        );
      } else {
        // Explicitly clear state if no rows come back
        setDistributions([]);
        setFilteredDistributions([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching distributions:", error);
      setError("Failed to load distribution data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply all filters
  const applyFilters = (data, branch, search, date, sizeRange) => {
    let filtered = [...data];

    // Branch filter
    if (branch) {
      filtered = filtered.filter(
        (item) => item.branchId === branch || item.branchId === String(branch)
      );
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.productCode?.toLowerCase().includes(searchLower) ||
          item.branchName?.toLowerCase().includes(searchLower) ||
          item.sizeRangeDisplay?.toLowerCase().includes(searchLower)
      );
    }

    // Date filter
    if (date !== "all") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (date === "today") {
        filtered = filtered.filter((item) => {
          const itemDate = new Date(item.createdDate);
          itemDate.setHours(0, 0, 0, 0);
          return itemDate.getTime() === today.getTime();
        });
      } else if (date === "week") {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        filtered = filtered.filter((item) => {
          const itemDate = new Date(item.createdDate);
          return itemDate >= weekAgo;
        });
      } else if (date === "month") {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        filtered = filtered.filter((item) => {
          const itemDate = new Date(item.createdDate);
          return itemDate >= monthAgo;
        });
      }
    }

    // Size range filter
    if (sizeRange) {
      filtered = filtered.filter((item) => item.sizeRangeDisplay === sizeRange);
    }

    setFilteredDistributions(filtered);
    calculateStats(filtered);
  };

  // ✅ Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedBranch, searchQuery]);

  // ✅ Handle search, limit, and page changes
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDistributions();
    }, 500);

    return () => clearTimeout(handler);
  }, [selectedBranch, searchQuery, page, pageSize]);

  // ✅ Handle client-side filters (date, size range)
  useEffect(() => {
    if (distributions.length > 0) {
      applyFilters(distributions, selectedBranch, "", dateFilter, sizeRangeFilter);
    }
  }, [dateFilter, sizeRangeFilter]);

  // ✅ Handle refresh
  const handleRefresh = () => {
    fetchDistributions();
  };

  // ✅ Clear all filters
  const handleClearFilters = () => {
    setSelectedBranch("");
    setSearchQuery("");
    setDateFilter("all");
    setSizeRangeFilter("");
  };

  // ✅ Check if filters are active
  const hasActiveFilters =
    selectedBranch || searchQuery || dateFilter !== "all" || sizeRangeFilter;

  // ✅ Load data on mount
  useEffect(() => {
    fetchBranches();
    fetchDistributions();
    fetchStats();
  }, []);

  // ✅ Fetch stats when branch changes
  useEffect(() => {
    fetchStats();
  }, [selectedBranch]);

  return (
    <Box sx={{ p: 3, bgcolor: "#F5F7FA", minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <WarehouseIcon sx={{ fontSize: 32, color: theme.primary, mr: 1 }} />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Warehouse Distribution Logs
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Track and monitor warehouse to retail distribution activities
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={6} sx={{ mb: 3 }}>
        {/* Total Distributions Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
              color: "white",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "150px",
                height: "150px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                top: "-50px",
                right: "-50px",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <LocalShippingIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Distributions
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="700">
                {stats.totalDistributions.toLocaleString()}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                {hasActiveFilters ? "Filtered View" : "All Records"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Cartons Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Total Cartons
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color={theme.primary}
                  >
                    {stats.totalCartons.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.uniqueProducts} products
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: theme.lightBg,
                    p: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <InventoryIcon sx={{ color: theme.primary, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Cost Card */}
        {/* <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Total Distribution Cost
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color={theme.success}
                  >
                    KSh{" "}
                    {stats.totalCost.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: KSh {stats.averageCostPerCarton.toFixed(2)}/carton
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: "#E8F5E9",
                    p: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <AttachMoneyIcon
                    sx={{ color: theme.success, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid> */}

        {/* Recent Activity Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Recent Activity
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="700"
                        color={theme.warning}
                      >
                        {stats.todayDistributions}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Today
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="700"
                        color={theme.warning}
                      >
                        {stats.weekDistributions}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This Week
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    bgcolor: "#FFF3E0",
                    p: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <CalendarTodayIcon
                    sx={{ color: theme.warning, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Actions Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          bgcolor: theme.cardBg,
        }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${[
                selectedBranch,
                searchQuery,
                dateFilter !== "all",
                sizeRangeFilter,
              ].filter(Boolean).length
                } Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by product code, branch, or size..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      {isSearching ? (
                        <CircularProgress size={20} />
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* Warehouse Filter */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={selectedBranch}
                label="Warehouse"
                onChange={(e) => setSelectedBranch(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Warehouses</em>
                </MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WarehouseIcon
                        sx={{ fontSize: 18, color: theme.primary }}
                      />
                      {branch.branchName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Date Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 18, color: theme.success }}
                    />
                    Today
                  </Box>
                </MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Size Range Filter */}
          {/* <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Size Range</InputLabel>
              <Select
                value={sizeRangeFilter}
                label="Size Range"
                onChange={(e) => setSizeRangeFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Sizes</em>
                </MenuItem>
                {sizeRanges.map((range) => (
                  <MenuItem key={range} value={range}>
                    {range}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}

          {/* Actions */}
          <Grid item xs={12} md={1.5}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {selectedBranch && (
              <Chip
                label={`Warehouse: ${branches.find((b) => b.id === selectedBranch)?.branchName ||
                  "Unknown"
                  }`}
                size="small"
                onDelete={() => setSelectedBranch("")}
                color="primary"
                variant="outlined"
              />
            )}
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFilter !== "all" && (
              <Chip
                label={`Date: ${dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)
                  }`}
                size="small"
                onDelete={() => setDateFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {sizeRangeFilter && (
              <Chip
                label={`Size: ${sizeRangeFilter}`}
                size="small"
                onDelete={() => setSizeRangeFilter("")}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Dynamic Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: theme.cardBg,
        }}
      >
        {loading && filteredDistributions.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            title="Warehouse Distribution Logs"
            subtitle={`Showing ${filteredDistributions.length} of ${rowCount} distributions ${hasActiveFilters ? "(filtered)" : ""
              }`}
            query={allwarehousedistribution}
            base_url={base_url}
            actions={actions}
            rows={filteredDistributions}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            paginationMode="server"
            pagination={true}
          />
        )}
      </Paper>
    </Box>
  );
}

export default WarehouseDistributionTable;
